/**
 * Command Palette Context
 * 
 * Provides a way to open the Command Palette from anywhere in the app.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

type CommandPaletteContextType = {
  isOpen: boolean;
  /** When true, any navigation from command palette will force a new tab (bypass dedup) */
  forceNewTab: boolean;
  open: () => void;
  /** Open command palette in "new tab mode" - any selection will open in a new tab */
  openForNewTab: () => void;
  close: () => void;
  toggle: () => void;
};

const CommandPaletteContext = createContext<CommandPaletteContextType>({
  isOpen: false,
  forceNewTab: false,
  open: () => {},
  openForNewTab: () => {},
  close: () => {},
  toggle: () => {},
});

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [forceNewTab, setForceNewTab] = useState(false);

  const open = useCallback(() => {
    setForceNewTab(false);
    setIsOpen(true);
  }, []);

  const openForNewTab = useCallback(() => {
    setForceNewTab(true);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setForceNewTab(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (prev) {
        // Closing - reset forceNewTab
        setForceNewTab(false);
      }
      return !prev;
    });
  }, []);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return (
    <CommandPaletteContext.Provider value={{ isOpen, forceNewTab, open, openForNewTab, close, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  );
};

export const useCommandPaletteContext = () => useContext(CommandPaletteContext);

export default CommandPaletteContext;

